/**
 * №13.1 (§1.2) — the generic launched-verb form. Opened by ContextActionBar for a
 * first-party contextual action: the matter/client context arrives from
 * `list_context_actions` **prefilled + locked** (rendered as lock chips), the
 * VERB_FORMS spec supplies the remaining inputs, and submit calls the verb with
 * the merged args. No per-page prefill wiring — the registry owns the context.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient, toMutationError } from "../../../lib/mcp-client";
import { showToast } from "../../components/primitives";
import { useLedgers } from "../../hooks/use-ledger-workbenches";
import { useEntityTypes } from "../../../lib/hooks/use-kuvio";
import { pluralize } from "../../../lib/query-keys";
import type { MutationError } from "../../../lib/types";
import { ArrangementPicker } from "./ArrangementPicker";
import { ResourcePicker } from "../../components/entity/ResourcePicker";
import type { ResolvedAction } from "../../hooks/use-context-actions";
import { conflictResultDisplay, type VerbField, type VerbFormSpec } from "./verb-forms";
import { FormError } from "./FormError";
import { completeAction, effectiveOnSuccess } from "./action-completion";

/**
 * №13.1 (design-pack §3) — the conflict-check result surface. `run_conflict_check`
 * returns the created `conflict-check` row; we render it here instead of a bare
 * toast (M16). The `View result` link opens the record page (M18/M19).
 */
interface ConflictResultView {
  id?: string;
  result: string;
  subjectNames: string;
  checkedBy: string;
  checkedAt: string;
  notes?: string;
}

function parseConflictResult(sc: unknown): ConflictResultView | null {
  if (!sc || typeof sc !== "object") return null;
  const o = sc as Record<string, unknown>;
  const row = (o.conflict_check as Record<string, unknown> | undefined) ?? undefined;
  const result = String(row?.result ?? o.result ?? "").trim();
  if (!result) return null;
  const id = String(row?.id ?? o.conflict_check_id ?? "") || undefined;
  return {
    id,
    result,
    subjectNames: String(row?.subject_names ?? ""),
    checkedBy: String(row?.checked_by ?? ""),
    checkedAt: String(row?.checked_at ?? ""),
    notes: row?.notes ? String(row.notes) : undefined,
  };
}

const labelOf = (row: Record<string, unknown>): string =>
  String(row.code ?? row.name ?? row.title ?? row.description ?? row.id);

export function VerbLaunchModal({
  action,
  spec,
  open,
  onClose,
  onDone,
}: {
  action: ResolvedAction;
  spec: VerbFormSpec;
  open: boolean;
  onClose: () => void;
  onDone?: (result: unknown) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const matterId = (action.prefill.matter_id ?? action.prefill.matter) as string | undefined;
  const fieldNames = useMemo(() => new Set(spec.fields.map((f) => f.name)), [spec.fields]);

  // №13.3 §2 — enum choices for a field with `optionsFrom` derive from the named
  // Tier-E type's field (its live, firm-configured option set), never a hardcoded
  // copy. Falls back to a literal `choices` when no optionsFrom is declared.
  const entityTypes = useEntityTypes();
  const choicesFor = (f: VerbField): string[] => {
    if (f.optionsFrom) {
      const t = entityTypes.data?.find((et) => et.slug === f.optionsFrom!.type);
      return t?.fields.find((fd) => fd.name === f.optionsFrom!.field)?.choices ?? [];
    }
    return f.choices ?? [];
  };

  // Locked prefill keys with NO matching field render as hard lock chips.
  const lockChips = action.locked.filter((k) => !fieldNames.has(k));

  const ledgers = useLedgers();
  const entitySources = useMemo(
    () => spec.fields.filter((f) => f.kind === "entities" && f.source).map((f) => f.source!),
    [spec.fields],
  );
  const entityOpts = useQuery({
    queryKey: ["verb-launch", "entities", action.verb, entitySources],
    enabled: open && entitySources.length > 0,
    staleTime: 15_000,
    queryFn: async () => {
      const out: Record<string, { id: string; label: string }[]> = {};
      for (const slug of entitySources) {
        const tool = `list_${pluralize(slug).replace(/-/g, "_")}`;
        try {
          const r = await mcpClient.toolsCall<{ entities: Record<string, unknown>[] }>(tool, { fields: ["*"] });
          out[slug] = ((r.structuredContent?.entities as Record<string, unknown>[]) ?? []).map((row) => ({ id: String(row.id), label: labelOf(row) }));
        } catch {
          out[slug] = [];
        }
      }
      return out;
    },
  });

  // Lazy-seed from the locked prefill + field defaults. The modal is mounted fresh
  // per launch (ContextActionBar renders it conditionally), so no reset effect is
  // needed — each open re-runs this initializer.
  const [values, setValues] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const f of spec.fields) {
      const pre = action.prefill[f.name];
      if (pre != null && pre !== "") seed[f.name] = String(pre);
      else if (f.default) seed[f.name] = f.default;
    }
    return seed;
  });
  const [arrangement, setArrangement] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<MutationError | string | null>(null);
  const [conflictResult, setConflictResult] = useState<ConflictResultView | null>(null);

  if (!open) return null;
  const set = (name: string, v: string) => setValues((s) => ({ ...s, [name]: v }));

  async function submit() {
    // Required-field check (arrangement fields are optional unless the picker resolves them).
    const missing = spec.fields.filter((f) => f.required && f.kind !== "arrangement" && !String(values[f.name] ?? "").trim());
    if (missing.length > 0) { setError(`Required: ${missing.map((f) => f.label).join(", ")}`); return; }
    setPending(true); setError(null);
    try {
      const payload: Record<string, unknown> = { ...action.prefill };
      for (const f of spec.fields) {
        if (f.kind === "arrangement") {
          if (arrangement && arrangement !== "__all__") payload[f.as ?? f.name] = arrangement;
          continue;
        }
        const raw = values[f.name];
        if (raw == null || raw === "") continue;
        if (f.kind === "number") payload[f.name] = Number(raw);
        else if (f.kind === "names") payload[f.name] = raw.split(/[;,]/).map((s) => s.trim()).filter(Boolean);
        else payload[f.name] = raw;
      }
      const r = await mcpClient.toolsCall(action.verb, payload);
      // №13.3 §1 — surface the verb's teaching error inline (never a bare string).
      if (r.isError) { setError(toMutationError(r)); setPending(false); return; }

      // §3 (M16): the conflict check surfaces its result IN PLACE — unchanged
      // path (invalidate, keep the modal open, render the result card).
      if (action.verb === "run_conflict_check") {
        for (const key of spec.invalidate ?? []) void qc.invalidateQueries({ queryKey: key });
        void qc.invalidateQueries({ queryKey: ["context-actions"] });
        onDone?.(r.structuredContent);
        const parsed = parseConflictResult(r.structuredContent);
        if (parsed) { setConflictResult(parsed); setPending(false); return; }
        showToast(spec.successMessage?.(r.structuredContent) ?? "Done", "success");
        onClose();
        return;
      }

      // №13.3 §1 — the completion contract: reactive-invalidate the originating
      // record's related lists, then route (open) or confirm-with-link (stay).
      const onSuccess = effectiveOnSuccess(action.verb, action.on_success);
      const target = spec.resultTarget?.(r.structuredContent) ?? null;
      completeAction({
        verb: action.verb,
        onSuccess,
        target,
        qc, navigate,
        message: spec.successMessage?.(r.structuredContent) ?? "Done",
        extraInvalidate: spec.invalidate,
      });
      onDone?.(r.structuredContent);
      onClose();
    } catch (e) {
      setError((e as Error)?.message ?? "Something went wrong.");
      setPending(false);
    }
  }

  const field = (f: VerbField) => {
    const v = values[f.name] ?? "";
    if (f.kind === "arrangement") {
      return <ArrangementPicker matterId={matterId} value={arrangement} onChange={setArrangement} label={f.label} />;
    }
    if (f.kind === "resource") {
      return <ResourcePicker value={v || null} onChange={(id) => set(f.name, id ?? "")} />;
    }
    if (f.kind === "ledger") {
      return (
        <select className="cem-input" value={v} onChange={(e) => set(f.name, e.target.value)}>
          <option value="">{ledgers.isLoading ? "Loading…" : "Select a trust ledger…"}</option>
          {(ledgers.data?.ledgers ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      );
    }
    if (f.kind === "entities") {
      const opts = entityOpts.data?.[f.source!] ?? [];
      return (
        <select className="cem-input" value={v} onChange={(e) => set(f.name, e.target.value)}>
          <option value="">{entityOpts.isLoading ? "Loading…" : `Select ${f.source}…`}</option>
          {opts.map((o) => <option key={o.id} value={o.id}>{o.label} ({o.id})</option>)}
        </select>
      );
    }
    if (f.kind === "enum") {
      return (
        <select className="cem-input" value={v} onChange={(e) => set(f.name, e.target.value)}>
          <option value="">Select…</option>
          {choicesFor(f).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      );
    }
    const htmlType = f.kind === "number" ? "number" : f.kind === "date" ? "date" : "text";
    return <input className="cem-input" type={htmlType} value={v} placeholder={f.placeholder} onChange={(e) => set(f.name, e.target.value)} />;
  };

  const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, fontFamily: "var(--font-sans)" };
  const box: React.CSSProperties = { background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-secondary)", borderRadius: "var(--border-radius-lg)", maxWidth: 460, width: "100%", maxHeight: "85vh", overflow: "auto", padding: 24 };

  // §3 (M16) — the conflict-check result surface replaces the form once the check
  // has run: a coloured banner + the names searched + checked-by/at + note, and a
  // link to the record page (M18). Read-only — no re-run (N9).
  if (conflictResult) {
    const disp = conflictResultDisplay(conflictResult.result);
    return (
      <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div role="dialog" aria-modal="true" style={box} className="vt">
          <div className="card card--accent" style={{ marginBottom: 14 }}>
            <div className="card__h">Conflict check result <span className="spacer" style={{ flex: 1 }} />
              <span className={`pill ${disp.pill}`}><span className="dot" />{disp.label}</span>
            </div>
            <div className="card__b">
              <dl className="particulars">
                <dt>Names searched</dt><dd>{conflictResult.subjectNames || "—"}</dd>
                <dt>Checked by</dt><dd>{conflictResult.checkedBy || "—"}</dd>
                <dt>Checked at</dt><dd>{conflictResult.checkedAt || "—"}</dd>
                <dt>Note</dt><dd className={conflictResult.notes ? "" : "muted"} style={{ whiteSpace: "pre-wrap" }}>{conflictResult.notes || "—"}</dd>
              </dl>
            </div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {conflictResult.id && (
              <Link className="sec" to={`/app/place/conflict-check/${conflictResult.id}`} onClick={onClose}>View result →</Link>
            )}
            <span className="spacer" style={{ flex: 1 }} />
            <button data-variant="primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" style={box} className="vt">
        <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 6px" }}>{spec.title ?? action.label}</h2>
        {spec.intro && <p className="tiny muted" style={{ margin: "0 0 14px" }}>{spec.intro}</p>}

        {lockChips.length > 0 && (
          <div className="stack" style={{ marginBottom: 14 }}>
            {lockChips.map((k) => (
              <div className="lockrow" key={k}>
                <span className="lk badge badge--soft">{k}</span>
                <span className="val">{String(action.prefill[k] ?? "—")}</span>
                <span className="pad">🔒 prefilled from context · not editable</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {spec.fields.map((f) => (
            <label key={f.name} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5 }}>
              <span style={{ color: "var(--color-text-secondary)" }}>{f.label}{f.required ? " *" : ""}</span>
              {field(f)}
            </label>
          ))}
        </div>

        <FormError error={error} />
        <div className="row" style={{ gap: 8, marginTop: 18 }}>
          <button onClick={onClose} disabled={pending}>Cancel</button>
          <span className="spacer" style={{ flex: 1 }} />
          <button data-variant="primary" onClick={() => void submit()} disabled={pending}>{pending ? "Working…" : spec.submitLabel}</button>
        </div>
      </div>
    </div>
  );
}
